import React from 'react'
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { generateDataLabelPluginConfig } from '../../utils/Config/DataLabelPluginConfig';

const BarChartProjectReport = ({ chart }) => {
    const { t } = useTranslation();
    const currentYear = new Date().getFullYear();
    const currentProjectionData = chart.map((item) =>
        item.year === currentYear ? item.currentValue ?? null : null
    );
    const hasCurrentProjection = currentProjectionData.some((value) => value !== null);

    const maxValue = chart?.length ? Math.max(
        1,
        ...chart.map((item, index) => Math.max(item.disbursed, item.projected, currentProjectionData[index] ?? 0))
    ) : 1;

    const roundedMaxValue = Math.ceil(maxValue / 1000000) * 1000000;

    const disbursedDataSource = chart.map((item) => item.disbursed);

    const data = {
        labels: chart.map((item) => item.year),
        datasets: [
            {
                label: t('amount_disbursed'),
                type: "bar",
                data: disbursedDataSource,
                backgroundColor: "#538cff",
                borderColor: "#538cff",
                borderWidth: 2,
                order: 2,
                barThickness: 15,
                barPercentage: 0.1,
                categoryPercentage: 0.5,
                datalabels: {
                    display: false
                }
            },
            {
                label: t('original_projection'),
                type: "line",
                data: chart.map((item) => item.projected),
                borderColor: "grey",
                pointBackgroundColor: "white",
                borderWidth: 3,
                fill: false,
                pointRadius: 6,
                lineTension: 0,
                barPercentage: 0.1,
                categoryPercentage: 0.5,
                order: 1,
                datalabels: {
                    anchor: 'center',
                    offset: 8,
                    align: ({ dataIndex, dataset }) => {
                        const values = dataset.data;
                        const isLastPoint = dataIndex === values.length - 1;
                        const currentValue = values[dataIndex];
                        const isDescending = isLastPoint
                            ? currentValue < values[dataIndex - 1]
                            : values[dataIndex + 1] < currentValue;
                        if (isLastPoint) return isDescending ? 225 : 135;
                        return isDescending ? 315 : 45;
                    },
                },
            },
            ...(hasCurrentProjection ? [{
                label: t('current_projection'),
                type: "bar",
                data: currentProjectionData,
                backgroundColor: "#7A8AA9",
                borderColor: "#7A8AA9",
                borderWidth: 2,
                order: 2,
                barThickness: 15,
                barPercentage: 0.1,
                categoryPercentage: 0.5,
                datalabels: {
                    display: (context) => {
                        const value = context.dataset.data[context.dataIndex];
                        return typeof value === 'number' && Number.isFinite(value);
                    },
                    align: 'end',
                    anchor: 'end',
                },
            }] : []),
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        devicePixelRatio: 3,
        layout: {
            padding: { bottom: 10, top: 8 }
        },
        plugins: {
            datalabels: {
                display: true,
                align: 'right',
                anchor: 'start',
                formatter: (value: number) => `${(value / 1000000).toFixed(1)} MM`,
                color: '#5A6A8A',
                font: {
                    size: 12,
                    weight: 300,
                },
            },
        },
        scales: {
            xAxes: [
                {
                    gridLines: { display: false },
                    ticks: { fontSize: 15 }
                },
            ],
            yAxes: [
                {
                    scaleLabel: {
                        display: true,
                        labelString: t('millions_word') + ' ' + t('dolar_sign'),
                        position: 'top',
                        padding: 10,
                    },
                    gridLines: { display: true },
                    ticks: {
                        fontSize: 15,
                        beginAtZero: true,
                        stepSize: Math.ceil(roundedMaxValue / 10),
                        min: 0,
                        max: roundedMaxValue,
                        callback: (value: number) => value / 1000000,
                    },
                },
            ],
        },
        legend: {
            display: false
        }
    };

    const customLabelsPlugin = generateDataLabelPluginConfig(disbursedDataSource);
    const currentProjectionOffsetPlugin = {
        afterDatasetUpdate: (_chartInstance, { index, meta }) => {
            if (!hasCurrentProjection || index !== 2) return;

            // Offset the bar model so labels and hit detection follow its position.
            meta.data.forEach((bar) => {
                bar._model.x += 30;
            });
        },
    };

    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            minHeight: 0,
            minWidth: 0,
            width: "100%",
            height: "100%",
            overflow: "hidden"
        }}>
            <Bar data={data} options={options} plugins={[customLabelsPlugin, currentProjectionOffsetPlugin]} />
        </div>
    );
};

export default BarChartProjectReport;

